/**
 * Semicircular Brand-Equity gauge (SYR-68 — matches the design hand-off
 * `.equity-gauge`). A 180° arc from "Weak −100" (left) to "+100 Strong"
 * (right); the gradient fill and the navy tick mark the position of the
 * bipolar equity score. The centre slot (`children`) holds the score number
 * and classification pill.
 *
 * Geometry mirrors the hand-off SVG exactly: viewBox 0 0 320 178, arc centre
 * (160,160), radius 130 → arc length = π·130 ≈ 408.4.
 */

import React from 'react';

const CX = 160;
const CY = 160;
const R = 130;
const ARC_LEN = Math.PI * R; // ≈ 408.4
const ARC_PATH = `M30 160 A${R} ${R} 0 0 1 290 160`;

export interface EquityArcGaugeProps {
  /** Bipolar equity score, −100…+100. */
  value: number;
  children?: React.ReactNode;
}

export const EquityArcGauge: React.FunctionComponent<EquityArcGaugeProps> = ({
  value,
  children,
}) => {
  const fraction = Math.max(0, Math.min(1, (value + 100) / 200));
  const dashoffset = ARC_LEN * (1 - fraction);
  // Tick position along the arc: angle sweeps 180° (left) → 0° (right).
  const angle = Math.PI * (1 - fraction);
  const tickX = CX + R * Math.cos(angle);
  const tickY = CY - R * Math.sin(angle);

  return (
    <div style={{ position: 'relative', maxWidth: 330, margin: '8px auto 0' }}>
      <svg
        viewBox="0 0 320 178"
        aria-hidden="true"
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="mvEqGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#EF4444" />
            <stop offset="0.5" stopColor="#F59E0B" />
            <stop offset="0.76" stopColor="#84CC16" />
            <stop offset="1" stopColor="#22C55E" />
          </linearGradient>
        </defs>
        <path d={ARC_PATH} fill="none" stroke="#EEF2F6" strokeWidth={16} strokeLinecap="round" />
        <path
          d={ARC_PATH}
          fill="none"
          stroke="url(#mvEqGrad)"
          strokeWidth={16}
          strokeLinecap="round"
          strokeDasharray={ARC_LEN}
          strokeDashoffset={dashoffset}
        />
        <circle cx={tickX} cy={tickY} r={9} fill="#fff" stroke="#0F172A" strokeWidth={3} />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 4,
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 9,
        }}
      >
        {children}
      </div>
    </div>
  );
};
